import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown, ChevronRight, Download, FileCheck2, RefreshCw, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import * as api from "@/api/supply";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";
import { SUPPLY_UI } from "./features";
import { channelLabel, fmtDateTime, int0 } from "./format";

const RUNS_KEY = ["supply", "shipment-runs"];

export function SupplyShipmentsPage(): React.ReactElement {
  const qc = useQueryClient();
  const runs = useQuery({ queryKey: RUNS_KEY, queryFn: api.getShipmentRuns });
  const [openRun, setOpenRun] = useState<number | null>(null);

  const delRun = useMutation({
    mutationFn: api.deleteShipmentRun,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RUNS_KEY });
      qc.invalidateQueries({ queryKey: ["supply", "analytics"] }); // бронь снята → доупак/движок пересчитать
      toast.success("Фиксация удалена, бронь снята");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось удалить")),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Отгрузки (зафиксированные поставки)</h2>
          <p className="text-sm text-muted-foreground">
            Из зафиксированного распределения — поставки по регионам. Скачай файл, загрузи на маркетплейс,
            впиши склад и номер поставки.
            {SUPPLY_UI.poolReserve && " Правка/удаление снимает бронь пула."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => runs.refetch()}
          className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-accent"
        >
          <RefreshCw className={cn("size-4", runs.isFetching && "animate-spin")} /> Обновить
        </button>
      </div>

      {runs.isLoading && <div className="py-10 text-center text-muted-foreground">Загрузка…</div>}
      {runs.data?.length === 0 && (
        <div className="rounded-md border border-dashed py-10 text-center text-sm text-muted-foreground">
          Пока нет зафиксированных распределений. Нажми «Зафиксировать потребность» на вкладке «Распределение».
        </div>
      )}

      <div className="space-y-2">
        {runs.data?.map((r) => (
          <div key={r.id} className="rounded-md border">
            <div
              className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-accent/40"
              onClick={() => setOpenRun(openRun === r.id ? null : r.id)}
            >
              {openRun === r.id ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
              <span className="font-medium">Фиксация #{r.id}</span>
              <span className="text-sm text-muted-foreground">{fmtDateTime(r.created_at)} · {r.created_by}</span>
              <span className="text-sm text-muted-foreground">· поставок: {r.shipments_count}</span>
              {SUPPLY_UI.poolReserve && <ReserveBadge status={r.reserve_status} />}
              {r.note && <span className="truncate text-sm text-muted-foreground">· {r.note}</span>}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Удалить фиксацию #${r.id} со всеми поставками? Бронь снимется.`))
                    delRun.mutate(r.id);
                }}
                className="ml-auto rounded p-1 text-muted-foreground hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400"
                title="Удалить фиксацию"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
            {openRun === r.id && <RunDetail runId={r.id} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function RunDetail({ runId }: { runId: number }): React.ReactElement {
  const qc = useQueryClient();
  const run = useQuery({ queryKey: ["supply", "shipment-run", runId], queryFn: () => api.getShipmentRun(runId) });

  if (run.isLoading) return <div className="px-4 py-3 text-sm text-muted-foreground">Загрузка поставок…</div>;
  // ⚠️ причину показываем словами бэкенда: «Не удалось загрузить» одинаково выглядело и у
  // удалённой фиксации (404 «фиксация не найдена»), и у упавшего запроса
  if (!run.data)
    return (
      <div className="px-4 py-3 text-sm text-destructive">
        ⚠ {extractErrorMessage(run.error, "Не удалось загрузить")}
      </div>
    );

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["supply", "shipment-run", runId] });
    qc.invalidateQueries({ queryKey: RUNS_KEY });
    qc.invalidateQueries({ queryKey: ["supply", "analytics"] });
  };

  return (
    <div className="border-t bg-muted/20 p-3">
      {/* `not_reserved` — бронь ХОТЕЛИ, но не смогли: это предупреждение. `none` — бронировать
          было нечего или механизм выключен осознанно: это просто факт, и жёлтым он пугал зря.
          Пока бронь выключена целиком, не показываем ни то, ни другое — даже на старых
          фиксациях: их статус правдив, но говорит менеджеру о проблеме, которой у него нет. */}
      {SUPPLY_UI.poolReserve && run.data.reserve_status === "not_reserved" && run.data.reserve_note && (
        <div className="mb-2 flex items-center gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
          <AlertTriangle className="size-3.5" /> Бронь не создана: {run.data.reserve_note}
        </div>
      )}
      {SUPPLY_UI.poolReserve && run.data.reserve_status === "none" && run.data.reserve_note && (
        <div className="mb-2 rounded border bg-muted/40 px-2 py-1 text-xs text-muted-foreground">
          Без брони: {run.data.reserve_note}
        </div>
      )}
      <div className="space-y-2">
        {run.data.shipments.map((sh) => (
          <ShipmentCard key={sh.id} sh={sh} onChange={invalidate} />
        ))}
      </div>
    </div>
  );
}

// бэкенд ставит этот сентинел, когда каскад не определил юрлицо (см. shipments._sku_and_legal)
const LEGAL_UNKNOWN = "—";

function ShipmentCard({ sh, onChange }: { sh: api.Shipment; onChange: () => void }): React.ReactElement {
  const [wh, setWh] = useState(sh.warehouse ?? "");
  const [num, setNum] = useState(sh.mp_supply_number ?? "");
  const legalUnknown = !sh.legal_name || sh.legal_name === LEGAL_UNKNOWN;
  // список юрлиц нужен только для починки — тянем лениво, когда есть что чинить
  const legals = useQuery({
    queryKey: ["supply", "legals"],
    queryFn: api.getLegals,
    enabled: legalUnknown,
    staleTime: 60 * 60_000,
  });

  const save = useMutation({
    mutationFn: (b: { warehouse?: string; mp_supply_number?: string; legal_name?: string }) =>
      api.patchShipment(sh.id, b),
    // юрлицо входит в имя файла и в шапку заказа 1С → после его правки перечитываем поставку
    onSuccess: (_d, b) => { toast.success("Сохранено"); if (b.legal_name) onChange(); },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось сохранить")),
  });
  const del = useMutation({
    mutationFn: () => api.deleteShipment(sh.id),
    onSuccess: () => { toast.success("Поставка удалена, бронь пересобрана"); onChange(); },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось удалить")),
  });
  const order1c = useMutation({
    mutationFn: () => api.createShipment1cOrder(sh.id),
    onSuccess: (s) => { toast.success(`Заказ 1С создан: № ${s.onec_order_number}`); onChange(); },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось создать заказ 1С")),
  });
  const noBarcode = sh.mp_code === "wb" ? sh.lines.filter((l) => !l.barcode).length : 0;
  const noArticle = sh.mp_code !== "wb" ? sh.lines.filter((l) => !l.article).length : 0;

  return (
    <div className="rounded-md border bg-background p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
          {channelLabel(sh.mp_code)}
        </span>
        {legalUnknown ? (
          <span className="flex items-center gap-1">
            <span
              className="flex items-center gap-1 rounded border border-red-300 bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300"
              title="Юрлицо не определилось ни по остаткам, ни по заказам DWH (новый SKU / лаг выгрузки). Заказ 1С по такой поставке заблокирован — выберите юрлицо вручную."
            >
              <AlertTriangle className="size-3" /> юрлицо не определено
            </span>
            <select
              value=""
              disabled={save.isPending}
              onChange={(e) => e.target.value && save.mutate({ legal_name: e.target.value })}
              className="h-6 rounded border bg-background px-1 text-xs focus:border-primary focus:outline-none"
            >
              <option value="">выбрать…</option>
              {(legals.data ?? []).map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </span>
        ) : (
          <span className="font-medium">{sh.legal_name}</span>
        )}
        <span className="text-muted-foreground">· {sh.region}</span>
        <span className="text-xs text-muted-foreground">
          · строк {sh.lines.length} · {int0(sh.lines.reduce((s, l) => s + l.qty, 0))} шт
        </span>
        {(noBarcode > 0 || noArticle > 0) && (
          <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400" title="эти строки не попадут в файл">
            <AlertTriangle className="size-3" /> без ключа: {noBarcode || noArticle}
          </span>
        )}
        <button
          type="button"
          onClick={() => api.downloadShipmentFile(sh).catch((e) => toast.error(extractErrorMessage(e, "Ошибка скачивания")))}
          className="ml-auto flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium hover:bg-accent"
        >
          <Download className="size-3.5" /> Файл для МП
        </button>
        {sh.onec_order_number ? (
          <span
            className="flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
            title={`Заказ покупателя в 1С уже создан (ref ${sh.onec_order_ref})`}
          >
            <FileCheck2 className="size-3.5" /> Заказ 1С: № {sh.onec_order_number}
          </span>
        ) : (
          <button
            type="button"
            disabled={order1c.isPending}
            onClick={() => { if (window.confirm(`Создать «Заказ покупателя» в 1С из поставки ${sh.legal_name}/${sh.region}? Файл прикрепится к заказу.`)) order1c.mutate(); }}
            className="flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium hover:bg-accent disabled:opacity-50"
            title="Создать документ «Заказ покупателя» в 1С и прикрепить файл поставки"
          >
            <FileCheck2 className={cn("size-3.5", order1c.isPending && "animate-pulse")} /> {order1c.isPending ? "Создаю…" : "Создать заказ в 1С"}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            // ⚠️ Документы 1С удалению не мешают и удалением НЕ снимаются — называем это вслух:
            // заказ держит резерв, а ПТМО уже перевела товар на юрлицо, и в учёте это остаётся
            const what = [
              `Удалить поставку ${sh.legal_name}/${sh.region}?`,
              sh.onec_order_number
                ? `Заказ 1С № ${sh.onec_order_number} и остальные документы по этой поставке ОСТАНУТСЯ в учёте — снимать их надо там же, руками.`
                : "",
              "Заявку Ozon удаление не трогает: если она создана, портал сначала попросит её отменить.",
            ]
              .filter(Boolean)
              .join("\n\n");
            if (window.confirm(what)) del.mutate();
          }}
          className="rounded p-1 text-muted-foreground hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400"
          title="Удалить поставку"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          Склад МП:
          <input
            value={wh}
            onChange={(e) => setWh(e.target.value)}
            onBlur={() => wh !== (sh.warehouse ?? "") && save.mutate({ warehouse: wh })}
            placeholder="выбрать после оформления на МП"
            className="h-7 w-48 rounded border bg-background px-1.5 text-sm focus:border-primary focus:outline-none"
          />
        </label>
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          № поставки:
          <input
            value={num}
            onChange={(e) => setNum(e.target.value)}
            onBlur={() => num !== (sh.mp_supply_number ?? "") && save.mutate({ mp_supply_number: num })}
            placeholder="номер с МП"
            className="h-7 w-36 rounded border bg-background px-1.5 text-sm focus:border-primary focus:outline-none"
          />
        </label>
      </div>
      {/* key по серверным qty → после refetch (правка/удаление) локальное состояние строк пересевается,
          а во время набора (сервер не менялся) key стабилен и не сбрасывает ввод */}
      <ShipmentLines key={sh.lines.map((l) => `${l.id}:${l.qty}`).join(",")} sh={sh} onChange={onChange} />
    </div>
  );
}

// Товары поставки: раскрытие + правка кол-ва / удаление строки (qty=0). Правка → пересбор брони.
function ShipmentLines({ sh, onChange }: { sh: api.Shipment; onChange: () => void }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState<Record<number, number>>(
    () => Object.fromEntries(sh.lines.map((l) => [l.id, l.qty])),
  );
  const save = useMutation({
    mutationFn: (line_qty: Record<number, number>) => api.patchShipment(sh.id, { line_qty }),
    onSuccess: () => { toast.success("Строки обновлены"); onChange(); },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось обновить строки")),
  });
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        {open ? "▾" : "▸"} Товары в поставке ({sh.lines.length}) — править / убрать
      </button>
      {open && (
        <div className="mt-1 overflow-x-auto rounded border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="px-2 py-1 text-left font-medium">Артикул</th>
                <th className="px-2 py-1 text-left font-medium">Товар</th>
                <th className="px-2 py-1 text-left font-medium">{sh.mp_code === "wb" ? "Штрихкод" : "Артикул МП"}</th>
                <th className="px-2 py-1 text-right font-medium">Кол-во</th>
                <th className="px-2 py-1" />
              </tr>
            </thead>
            <tbody>
              {sh.lines.map((l) => {
                const key = sh.mp_code === "wb" ? l.barcode : l.article;
                return (
                  <tr key={l.id} className="border-b last:border-0">
                    <td className="px-2 py-1 font-mono text-xs">{l.article || "—"}</td>
                    <td className="max-w-[260px] px-2 py-1">
                      <span className="flex items-center gap-1.5">
                        <span className="block truncate" title={l.nom_name}>{l.nom_name}</span>
                        {l.legal_source?.endsWith("_ambiguous") && (
                          <span
                            className="shrink-0 rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                            title="Источники дали одинаковый вес по обоим юрлицам — победитель выбран произвольно. Проверьте юрлицо поставки перед созданием заказа 1С."
                          >
                            юрлицо?
                          </span>
                        )}
                      </span>
                    </td>
                    <td className={cn("px-2 py-1 font-mono text-xs", key ? "text-muted-foreground" : "text-amber-600 dark:text-amber-400")}>
                      {key || "нет ключа"}
                    </td>
                    <td className="px-2 py-1 text-right">
                      <input
                        type="number"
                        min={0}
                        value={qty[l.id] ?? l.qty}
                        onChange={(e) => setQty((s) => ({ ...s, [l.id]: Number(e.target.value) || 0 }))}
                        onBlur={() => (qty[l.id] ?? l.qty) !== l.qty && save.mutate({ [l.id]: qty[l.id] ?? l.qty })}
                        className="h-6 w-16 rounded border bg-background px-1 text-right text-sm focus:border-primary focus:outline-none"
                      />
                    </td>
                    <td className="px-2 py-1 text-right">
                      <button
                        type="button"
                        onClick={() => window.confirm(`Убрать ${l.article || l.nom_name} из поставки?`) && save.mutate({ [l.id]: 0 })}
                        className="rounded p-0.5 text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                        title="Убрать товар из поставки"
                      >
                        <Trash2 className="size-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReserveBadge({ status }: { status: string }): React.ReactElement | null {
  if (status === "reserved")
    return <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">забронировано</span>;
  if (status === "not_reserved")
    return <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-700 dark:bg-amber-950 dark:text-amber-300">без брони</span>;
  if (status === "none")  // бронировать было нечего (нет флаконных позиций) — не молча, видимый бейдж
    return <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">без брони</span>;
  return null;
}
